import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PayCycleStatus, PayCycle, PayCycleEmployee, PayCycleSettings, PayrollExclusion, PayCycleFilters } from "../types/PayCycleContext.types";
import { usePayCycleHelpers } from "./PayCycleContext.helpers";


import { logger } from '../utils/logger';
// Re-export types for backward compatibility
export type { PayCycleStatus, PayCycle, PayCycleEmployee, PayCycleSettings, PayrollExclusion, PayCycleFilters };

type PayCycleContextValue = {
  // Main pay cycle data
  payCycles: PayCycle[];
  currentCycle: PayCycle | null;
  loading: boolean;
  
  // Cycle management
  createCycle: (settings: PayCycleSettings) => Promise<void>;
  updateCycle: (cycleId: string, updates: Partial<PayCycle>) => Promise<void>;
  deleteCycle: (cycleId: string) => Promise<void>;
  duplicateCycle: (cycleId: string) => Promise<void>;
  
  // Employee management within cycles
  cycleEmployees: PayCycleEmployee[];
  excludedEmployees: PayrollExclusion[];
  
  // Processing
  processPayroll: (cycleId: string) => Promise<void>;
  approvePayroll: (cycleId: string) => Promise<void>;
  generatePayslips: (cycleId: string) => Promise<void>;
  
  // Filters and search
  filters: PayCycleFilters;
  setFilters: (filters: PayCycleFilters) => void;
  
  // Actions
  refreshData: () => Promise<void>;
  exportPayrollReport: (cycleId: string) => void;
  addExclusion: (exclusion: Omit<PayrollExclusion, 'id'>) => Promise<void>;
  removeExclusion: (exclusionId: string) => Promise<void>;
  setCycleDisabledStatus: (cycleId: string, disabled: boolean) => Promise<void>;
};

const PayCycleContext = createContext<PayCycleContextValue | null>(null);

export function PayCycleProvider({ children }: { children: React.ReactNode }) {
  const [payCycles, setPayCycles] = useState<PayCycle[]>([]);
  const [currentCycle, setCurrentCycle] = useState<PayCycle | null>(null);
  const [cycleEmployees, setCycleEmployees] = useState<PayCycleEmployee[]>([]);
  const [excludedEmployees, setExcludedEmployees] = useState<PayrollExclusion[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<PayCycleFilters>({
    status: 'all',
    frequency: 'all',
    dateRange: {
      start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    searchTerm: ''
  });

  const {
    refreshData,
    setCycleDisabledStatus,
    createCycle,
    updateCycle,
    deleteCycle,
    duplicateCycle,
    processPayroll,
    approvePayroll,
    generatePayslips,
    addExclusion,
    removeExclusion,
  } = usePayCycleHelpers(setPayCycles, setCurrentCycle, setCycleEmployees, setExcludedEmployees, setLoading);

  const exportPayrollReport = useCallback((cycleId: string) => {
    const cycle = payCycles.find(c => c.id === cycleId);
    logger.debug(`Exporting payroll report for cycle ${cycleId}:`, cycle);
    // In real app, this would trigger a file download
  }, [payCycles]);

  // Initial data load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const value = useMemo(() => ({
    payCycles,
    currentCycle,
    loading,
    createCycle,
    updateCycle,
    deleteCycle,
    duplicateCycle,
    cycleEmployees,
    excludedEmployees,
    processPayroll,
    approvePayroll,
    generatePayslips,
    filters,
    setFilters,
    refreshData,
    exportPayrollReport,
    addExclusion,
    removeExclusion,
    setCycleDisabledStatus,
  }), [
    payCycles,
    currentCycle,
    loading,
    cycleEmployees,
    excludedEmployees,
    filters,
    createCycle,
    updateCycle,
    deleteCycle,
    duplicateCycle,
    processPayroll,
    approvePayroll,
    generatePayslips,
    refreshData,
    exportPayrollReport,
    addExclusion,
    removeExclusion,
    setCycleDisabledStatus,
  ]);

  return (
    <PayCycleContext.Provider value={value}>
      {children}
    </PayCycleContext.Provider>
  );
}

export function usePayCycle() {
  const context = useContext(PayCycleContext);
  if (!context) {
    throw new Error('usePayCycle must be used within a PayCycleProvider');
  }
  return context;
}
